import { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Stack,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react';

import { getBackendUrl, isAuth0Initialized, notifySessionEstablished, storePasswordSession } from '../lib/auth0-client';
import type { AuthMeResponse } from '../lib/auth0-client';
import { useAuthTranslation, mapAuthError } from '../i18n/auth-i18n';
import type { ProxyErrorBody } from '../i18n/auth-i18n';

export type AuthFormMode = 'login' | 'signup' | 'forgotPassword';

interface PasswordRule {
  key: string;
  test: (pw: string) => boolean;
}

const PASSWORD_RULES: PasswordRule[] = [
  { key: 'auth.form.rules.length', test: (pw) => pw.length >= 8 },
  { key: 'auth.form.rules.lowercase', test: (pw) => /[a-z]/.test(pw) },
  { key: 'auth.form.rules.uppercase', test: (pw) => /[A-Z]/.test(pw) },
  { key: 'auth.form.rules.number', test: (pw) => /\d/.test(pw) },
  { key: 'auth.form.rules.special', test: (pw) => /[^a-zA-Z0-9]/.test(pw) },
];

const MIN_TYPES_REQUIRED = 3;

function getPasswordStrength(password: string) {
  const lengthOk = PASSWORD_RULES[0].test(password);
  const typesPassed = PASSWORD_RULES.slice(1).filter((r) => r.test(password)).length;
  return { lengthOk, typesPassed, isValid: lengthOk && typesPassed >= MIN_TYPES_REQUIRED };
}

export interface EmailPasswordFormProps {
  /** Which credential flow this form runs. The route owns the mode. */
  mode: AuthFormMode;
  /**
   * Fired after a successful submit. For signup, receives the email that was
   * registered so the page can route to /verify-email. For login it fires
   * after notifySessionEstablished. Not used for forgotPassword (inline copy).
   */
  onSuccess?: (email: string) => void;
  /** Login only: the account exists but its email is unverified (typed 403). */
  onEmailNotVerified?: (email: string) => void;
  /** Locks the form while another flow (e.g. Microsoft redirect) is running. */
  disabled?: boolean;
}

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

const PRESS_FEEDBACK_SX = {
  transition: `transform 160ms ${EASE_OUT}, box-shadow 200ms ease, background-color 200ms ease`,
  '&:active': { transform: 'scale(0.97)' },
  '&.Mui-disabled': { transform: 'none' },
} as const;

// Inputs and the submit button share this exact height for a tidy stack.
const FIELD_HEIGHT = 44;

// Eye toggle cross-fades both glyphs (kept in the DOM, one overlaid) per the
// design-eng spec — scale 0.25↔1, opacity, blur 4↔0. No motion library needed;
// the global prefers-reduced-motion rule neutralises the transition.
const EYE_FADE =
  'opacity 300ms cubic-bezier(0.2, 0, 0, 1), transform 300ms cubic-bezier(0.2, 0, 0, 1), filter 300ms cubic-bezier(0.2, 0, 0, 1)';

const eyeLayerSx = (visible: boolean) =>
  ({
    position: 'absolute',
    inset: 0,
    display: 'inline-flex',
    transition: EYE_FADE,
    opacity: visible ? 1 : 0,
    transform: visible ? 'scale(1)' : 'scale(0.25)',
    filter: visible ? 'blur(0px)' : 'blur(4px)',
  } as const);

const inputSx = (theme: import('@mui/material/styles').Theme) => ({
  '& .MuiOutlinedInput-root': {
    height: FIELD_HEIGHT,
    borderRadius: '8px',
    backgroundColor: alpha(theme.palette.common.white, 0.03),
    transition: 'border-color 200ms ease, box-shadow 200ms ease, background-color 200ms ease',
    '& fieldset': { borderColor: alpha(theme.palette.common.white, 0.12), transition: 'border-color 200ms ease' },
    '&:hover fieldset': { borderColor: alpha(theme.palette.common.white, 0.24) },
    '&.Mui-focused fieldset': { borderColor: alpha(theme.palette.common.white, 0.45), borderWidth: '1px' },
    '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.common.white, 0.07)}` },
  },
});

async function readErrorBody(response: Response): Promise<ProxyErrorBody> {
  try {
    return (await response.json()) as ProxyErrorBody;
  } catch {
    return {};
  }
}

export function EmailPasswordForm({ mode, onSuccess, onEmailNotVerified, disabled = false }: EmailPasswordFormProps) {
  const { t, i18n } = useAuthTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const passwordStrength = useMemo(() => getPasswordStrength(password), [password]);
  const isBusy = loading || disabled;

  const post = useCallback((path: string, payload: Record<string, unknown>) => {
    if (!isAuth0Initialized()) throw new Error('Auth backend not initialized');
    return fetch(`${getBackendUrl()}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  }, []);

  const handleLogin = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await post('/auth/password/login', { email, password });
      if (response.ok) {
        // Bootstrap the in-memory session from the body so cross-origin dev
        // (localhost, where the .ari-os.com cookie isn't visible) still works,
        // then nudge useAuth0Auth to re-resolve.
        const data = (await response.json().catch(() => null)) as AuthMeResponse | null;
        if (data) storePasswordSession(data);
        notifySessionEstablished();
        onSuccess?.(email);
        return;
      }
      const body = await readErrorBody(response);
      if (response.status === 403 && body.error === 'email_not_verified') {
        onEmailNotVerified?.(email);
        return;
      }
      setError(t(mapAuthError('login', body, response.status)));
    } catch {
      setError(t('auth.errors.loginFailed'));
    } finally {
      setLoading(false);
    }
  }, [post, email, password, onSuccess, onEmailNotVerified, t]);

  const handleSignup = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Forward the active UI language so Auth0 stores it on the new user
      // (user_metadata.lang) and can localize the verification / reset emails
      // it sends. resolvedLanguage is the concrete locale i18next settled on.
      const response = await post('/auth/password/signup', {
        email,
        password,
        lang: i18n.resolvedLanguage ?? i18n.language,
      });
      if (!response.ok) {
        const body = await readErrorBody(response);
        setError(t(mapAuthError('signup', body, response.status)));
        return;
      }
      // Signup mints no session — the page routes to /verify-email.
      onSuccess?.(email);
    } catch {
      setError(t('auth.errors.signupFailed'));
    } finally {
      setLoading(false);
    }
  }, [post, email, password, onSuccess, t, i18n]);

  const handleForgotPassword = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await post('/auth/password/forgot', { email });
      if (!response.ok) {
        const body = await readErrorBody(response);
        setError(t(mapAuthError('forgotPassword', body, response.status)));
        return;
      }
      setSuccess(t('auth.form.forgotSuccess'));
    } catch {
      setError(t('auth.errors.forgotFailed'));
    } finally {
      setLoading(false);
    }
  }, [post, email, t]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      // Guard against a fast double-submit before disabled reflects in the DOM.
      if (isBusy) return;
      if (mode === 'login') handleLogin();
      else if (mode === 'signup') handleSignup();
      else handleForgotPassword();
    },
    [isBusy, mode, handleLogin, handleSignup, handleForgotPassword]
  );

  const buttonText = () => {
    if (loading) return <CircularProgress size={20} color="inherit" />;
    if (mode === 'signup') return t('auth.signup.submit');
    if (mode === 'forgotPassword') return t('auth.forgotPassword.submit');
    return t('auth.login.submit');
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2.5 }}>
          {success}
        </Alert>
      )}

      <Stack spacing={2.25}>
        <Box>
          <Typography variant="body2" sx={{ mb: 0.75, color: 'text.secondary', fontWeight: 500 }}>
            {t('auth.form.emailLabel')}
          </Typography>
          <TextField
            fullWidth
            placeholder={t('auth.form.emailPlaceholder')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isBusy}
            autoComplete="email"
            sx={inputSx}
          />
        </Box>

        {mode !== 'forgotPassword' && (
          <Box>
            <Typography variant="body2" sx={{ mb: 0.75, color: 'text.secondary', fontWeight: 500 }}>
              {t('auth.form.passwordLabel')}
            </Typography>
            <TextField
              fullWidth
              placeholder={t('auth.form.passwordPlaceholder')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isBusy}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? t('auth.form.hidePassword') : t('auth.form.showPassword')}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      disabled={isBusy}
                      size="small"
                      sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
                    >
                      {/* Cross-fade both glyphs rather than hard-swapping. */}
                      <Box sx={{ position: 'relative', width: 20, height: 20 }}>
                        <Box sx={eyeLayerSx(!showPassword)}>
                          <Icon icon="eva:eye-outline" width={20} height={20} />
                        </Box>
                        <Box sx={eyeLayerSx(showPassword)}>
                          <Icon icon="eva:eye-off-outline" width={20} height={20} />
                        </Box>
                      </Box>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {mode === 'signup' && password.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                  {t('auth.form.passwordRequirements')}
                </Typography>
                {PASSWORD_RULES.map((rule) => {
                  const passed = rule.test(password);
                  return (
                    <Stack key={rule.key} direction="row" spacing={0.75} alignItems="center">
                      <Box
                        sx={{
                          display: 'inline-flex',
                          color: passed ? 'success.main' : 'text.disabled',
                          transition: 'color 200ms ease',
                        }}
                      >
                        <Icon
                          icon={passed ? 'eva:checkmark-circle-2-fill' : 'eva:radio-button-off-outline'}
                          width={16}
                          height={16}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ color: passed ? 'success.main' : 'text.disabled', transition: 'color 200ms ease' }}
                      >
                        {t(rule.key)}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}

        <Button
          fullWidth
          type="submit"
          variant="contained"
          disabled={
            isBusy ||
            !email ||
            (mode !== 'forgotPassword' && !password) ||
            (mode === 'signup' && !passwordStrength.isValid)
          }
          sx={{
            ...PRESS_FEEDBACK_SX,
            height: FIELD_HEIGHT,
            mt: 1,
            fontWeight: 600,
            borderRadius: '8px',
            backgroundColor: '#E5E5E5',
            color: '#171717',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#D6D6D6', boxShadow: 'none' },
            '&.Mui-disabled': {
              transform: 'none',
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
              color: 'rgba(255, 255, 255, 0.4)',
            },
          }}
        >
          {buttonText()}
        </Button>
      </Stack>
    </Box>
  );
}
